import React, { useMemo } from 'react';
import { palette, typography, spacing, withOpacity } from '../../utils/designTokens';

// Business Task Anomaly shape: how a run's behavior metrics sit against its task baseline.
// Two fixed groups cover all 14 detector features, so every run is drawn on the same axes.
// Input is the detector's `all_deviations` (one entry per feature: feature, actual,
// baseline_mean, z_score, zero_variance). Only the demo produces it today.
// An optional `rolling` deviation list (also measured against the baseline) is drawn as a
// dotted ring: the drift view uses it to show where the current model's baseline has moved.

const WORKLOAD = [
  ['total_input_tokens', 'Input tokens'],
  ['duration_per_span', 'Duration per span'],
  ['duration_ms', 'Total duration'],
  ['cache_hit_rate', 'Cache hit rate'],
  ['output_input_ratio', 'Output-to-input ratio'],
  ['total_output_tokens', 'Output tokens'],
];

const EXECUTION = [
  ['span_count', 'Span count'],
  ['tool_share', 'Tool span share'],
  ['repeated_tool_ratio', 'Repeated tool calls'],
  ['llm_share', 'LLM span share'],
  ['retriever_share', 'Retriever span share'],
  ['model_count', 'Model count'],
  ['agent_count', 'Agent count'],
  ['error_count', 'Error count'],
];

const Z_MAX = 4;          // radius runs from −4σ at the center to +4σ at the edge
const MOVED = 2;          // |z| at or beyond the ±2σ typical range
const SIZE = { w: 520, h: 420, cx: 260, cy: 210, r: 140 };

const formatZ = (z) => `${z > 0 ? '+' : z < 0 ? '−' : '±'}${Math.abs(z).toFixed(1)}σ`;
const formatRaw = (v) => (Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100));
const clampZ = (z) => Math.max(-Z_MAX, Math.min(Z_MAX, z));

const angleAt = (i, n) => -Math.PI / 2 + (2 * Math.PI * i) / n;
const radiusFor = (z) => (SIZE.r * (clampZ(z) + Z_MAX)) / (2 * Z_MAX);
const pointAt = (i, n, z) => {
  const a = angleAt(i, n);
  const r = radiusFor(z);
  return [SIZE.cx + r * Math.cos(a), SIZE.cy + r * Math.sin(a)];
};
const polygon = (values, n) => values.map((z, i) => pointAt(i, n, z).map(v => v.toFixed(1)).join(',')).join(' ');

// Look up each run's deviation for every axis in a group; null where the feature is absent.
const groupRows = (axes, runs) => axes.map(([feature, label]) => ({
  feature,
  label,
  values: runs.map(run => run.byFeature[feature] || null),
}));

const isMover = (row) => !row.values.some(v => v?.zero_variance) && row.values.some(v => v && Math.abs(v.z_score) >= MOVED);

function Radar({ title, question, rows, runs, overlay, rolling }) {
  const n = rows.length;
  const ring = (z) => rows.map((_, i) => pointAt(i, n, z).map(v => v.toFixed(1)).join(',')).join(' ');
  const bandPath = (() => {
    const outer = rows.map((_, i) => pointAt(i, n, MOVED));
    const inner = rows.map((_, i) => pointAt(i, n, -MOVED)).reverse();
    const toPath = pts => pts.map(p => p.join(',')).join('L');
    return `M${toPath(outer)}Z M${toPath(inner)}Z`;
  })();
  const bisector = -Math.PI / 2 + Math.PI / n;
  const baselineColor = palette.accents.secondary.base;
  const runColor = palette.severity.medium.text;

  return (
    <div className={`flex min-w-0 flex-col ${spacing.gap.xs}`}>
      <div className={`${typography.size.sm} ${typography.weight.semibold}`} style={{ color: palette.text.primary }}>{title}</div>
      <div className={typography.size.xs} style={{ color: palette.text.muted }}>{question}</div>
      <svg viewBox={`0 0 ${SIZE.w} ${SIZE.h}`} className="block h-auto w-full overflow-visible" role="img" aria-label={`${title} radar`}>
        <path d={bandPath} fill={withOpacity(baselineColor, 12)} fillRule="evenodd" stroke="none" />
        {[-MOVED, MOVED, Z_MAX].map(z => (
          <polygon key={z} points={ring(z)} fill="none" stroke={palette.support.gridline} strokeWidth={1} />
        ))}
        {rows.map((row, i) => {
          const [x, y] = pointAt(i, n, Z_MAX);
          return <line key={row.feature} x1={SIZE.cx} y1={SIZE.cy} x2={x} y2={y} stroke={palette.support.gridline} strokeWidth={1} />;
        })}
        <polygon points={ring(0)} fill="none" stroke={baselineColor} strokeWidth={2} strokeDasharray="6 5" />
        {[[-MOVED, '−2σ'], [0, '0σ'], [MOVED, '+2σ'], [Z_MAX, '+4σ']].map(([z, text]) => {
          const r = radiusFor(z);
          return (
            <text key={text} x={SIZE.cx + r * Math.cos(bisector) + 3} y={SIZE.cy + r * Math.sin(bisector) + 3}
              className={`${typography.size.metadata} ${typography.family.mono}`} fill={palette.text.muted}>{text}</text>
          );
        })}

        {rolling && (
          <polygon
            points={polygon(rows.map(row => rolling[row.feature]?.z_score ?? 0), n)}
            fill="none"
            stroke={palette.text.supporting}
            strokeWidth={2}
            strokeDasharray="1.5 4"
            strokeLinecap="round"
            strokeLinejoin="round">
            <title>Rolling baseline</title>
          </polygon>
        )}

        {runs.map((run, ri) => (
          <polygon key={run.name || ri}
            points={polygon(rows.map(row => row.values[ri]?.z_score ?? 0), n)}
            fill={overlay ? 'none' : palette.severity.medium.bg}
            stroke={runColor}
            strokeWidth={overlay ? 1.5 : 2}
            strokeOpacity={overlay ? 0.7 : 1}
            strokeLinejoin="round">
            <title>{run.name}</title>
          </polygon>
        ))}

        {rows.map((row, i) => {
          const a = angleAt(i, n);
          const cos = Math.cos(a);
          const sin = Math.sin(a);
          const lx = SIZE.cx + (SIZE.r + 22) * cos;
          const ly = SIZE.cy + (SIZE.r + 22) * sin;
          const anchor = Math.abs(cos) < 0.2 ? 'middle' : cos > 0 ? 'start' : 'end';
          const dy = sin < -0.5 ? -10 : sin > 0.5 ? 8 : 0;
          const present = row.values.filter(Boolean);
          const zeroVariance = present.some(v => v.zero_variance);
          const moved = isMover(row);

          let sub;
          if (!present.length) sub = '–';
          else if (zeroVariance) sub = `○ ${formatRaw(present[0].actual)} (always ${formatRaw(present[0].baseline_mean)})`;
          else if (overlay) {
            const zs = present.map(v => v.z_score);
            sub = moved ? `${zs[0] >= 0 ? '▲' : '▼'} ${formatZ(Math.min(...zs))} to ${formatZ(Math.max(...zs))}` : '✓ steady';
          } else {
            const z = present[0].z_score;
            sub = `${moved ? (z >= 0 ? '▲' : '▼') : '✓'} ${formatZ(z)}`;
          }

          const tooltip = zeroVariance
            ? `${row.label}: ${formatRaw(present[0].actual)}, baseline is always ${formatRaw(present[0].baseline_mean)}, so σ does not apply`
            : `${row.label}: ${present.map((v, k) => `${overlay ? `${runs[k].name} ` : ''}${formatZ(v.z_score)}`).join(', ')}`
              + (rolling?.[row.feature] ? `; rolling baseline ${formatZ(rolling[row.feature].z_score)}` : '');

          return (
            <g key={row.feature}>
              {!overlay && present[0] && (() => {
                const [px, py] = pointAt(i, n, present[0].z_score);
                return zeroVariance
                  ? <circle cx={px} cy={py} r={4.5} fill="none" stroke={palette.text.muted} strokeWidth={2} />
                  : <circle cx={px} cy={py} r={4} fill={runColor} />;
              })()}
              <text x={lx} y={ly + dy} textAnchor={anchor}
                className={`${typography.size.xs} ${typography.weight.medium}`} fill={palette.text.primary}>{row.label}</text>
              <text x={lx} y={ly + dy + 16} textAnchor={anchor}
                className={`${typography.size.label} ${typography.family.mono}`}
                fill={moved ? runColor : palette.text.muted}>{sub}</text>
              <line x1={SIZE.cx} y1={SIZE.cy} x2={pointAt(i, n, Z_MAX)[0]} y2={pointAt(i, n, Z_MAX)[1]} stroke="transparent" strokeWidth={26}>
                <title>{tooltip}</title>
              </line>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function AnomalyShapeRadars({ runs = [], rolling = null, labels = {} }) {
  const prepared = useMemo(() => (Array.isArray(runs) ? runs : [])
    .filter(run => Array.isArray(run?.all_deviations) && run.all_deviations.length > 0)
    .map(run => ({
      name: run.name || '',
      byFeature: Object.fromEntries(run.all_deviations.map(d => [d.feature, d])),
    })), [runs]);

  if (prepared.length === 0) return null;

  const overlay = prepared.length > 1;
  const rollingByFeature = Array.isArray(rolling) && rolling.length > 0
    ? Object.fromEntries(rolling.map(d => [d.feature, d]))
    : null;
  const workloadQuestion = labels.workloadQuestion
    || (overlay ? 'How heavy and costly were the runs?' : 'How heavy and costly was the run?');
  const executionQuestion = labels.executionQuestion
    || (overlay ? 'Did they run the usual mix of steps?' : 'Did it run the usual mix of steps?');
  const workloadRows = groupRows(WORKLOAD, prepared);
  const executionRows = groupRows(EXECUTION, prepared);

  return (
    <div className={`flex flex-col ${spacing.gap.md}`}>
      <div className={`grid grid-cols-[repeat(auto-fit,minmax(360px,1fr))] ${spacing.gap.lg}`}>
        <Radar title="Workload" question={workloadQuestion}
          rows={workloadRows} runs={prepared} overlay={overlay} rolling={rollingByFeature} />
        <Radar title="Execution" question={executionQuestion}
          rows={executionRows} runs={prepared} overlay={overlay} rolling={rollingByFeature} />
      </div>

      <div className={`flex flex-wrap ${spacing.gap.lg} ${typography.size.xs}`} style={{ color: palette.text.secondary }}>
        <span className={`inline-flex items-center ${spacing.gap.sm}`}>
          <svg width="22" height="4" aria-hidden="true"><line x1="0" y1="2" x2="22" y2="2" stroke={palette.severity.medium.text} strokeWidth={2} /></svg>
          {labels.run || (overlay ? `Each run (${prepared.length})` : 'This run')}
        </span>
        <span className={`inline-flex items-center ${spacing.gap.sm}`}>
          <svg width="22" height="4" aria-hidden="true"><line x1="0" y1="2" x2="22" y2="2" stroke={palette.accents.secondary.base} strokeWidth={2} strokeDasharray="6 5" /></svg>
          {labels.baseline || 'Task baseline'}
        </span>
        {rollingByFeature && (
          <span className={`inline-flex items-center ${spacing.gap.sm}`}>
            <svg width="22" height="4" aria-hidden="true"><line x1="0" y1="2" x2="22" y2="2" stroke={palette.text.supporting} strokeWidth={2} strokeDasharray="1.5 4" strokeLinecap="round" /></svg>
            {labels.rolling || 'Rolling baseline'}
          </span>
        )}
        <span className={`inline-flex items-center ${spacing.gap.sm}`}>
          <svg width="14" height="10" aria-hidden="true"><rect width="14" height="10" fill={withOpacity(palette.accents.secondary.base, 12)} /></svg>
          Typical range (±2σ)
        </span>
        <span className={`inline-flex items-center ${spacing.gap.sm}`}>
          <svg width="12" height="12" aria-hidden="true"><circle cx="6" cy="6" r="4" fill="none" stroke={palette.text.muted} strokeWidth={2} /></svg>
          Never varies in baseline: shown as a value
        </span>
      </div>
    </div>
  );
}
